import { useCallback, useEffect, useMemo, useState } from 'react';
import { InternalUser } from '../../../types';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { extractApiErrorMessage } from '../../../utils/extractApiErrorMessage';
import { bulkSetBlockList, fetchInternalUsers } from './blockListApi';

const blockedIdsFor = (users: InternalUser[], listType: string): string[] =>
  users.filter((user) => user.block_matrix?.[listType]).map((user) => user.id);

export interface UseBlockListReturn {
  internalUsers: InternalUser[];
  listType: string;
  stagedIds: string[];
  loading: boolean;
  saving: boolean;
  dirty: boolean;
  setStagedIds: (ids: string[]) => void;
  selectListType: (listType: string) => void;
  save: () => Promise<void>;
  cancel: () => void;
}

export const useBlockList = (initialListType: string): UseBlockListReturn => {
  const { setSnack } = useSnackbar();
  const [internalUsers, setInternalUsers] = useState<InternalUser[]>([]);
  const [listType, setListType] = useState(initialListType);
  const [committedIds, setCommittedIds] = useState<string[]>([]);
  const [stagedIds, setStagedIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchInternalUsers().then(({ data, error }) => {
      if (!active) return;
      if (error)
        setSnack({ message: extractApiErrorMessage(error, 'Could not load users'), type: 'error', open: true });
      const users = data ?? [];
      const blocked = blockedIdsFor(users, listType);
      setInternalUsers(users);
      setCommittedIds(blocked);
      setStagedIds(blocked);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, [listType, setSnack]);

  const selectListType = useCallback((next: string) => setListType(next), []);

  const dirty = useMemo(() => {
    if (committedIds.length !== stagedIds.length) return true;
    const committedSet = new Set(committedIds);
    return stagedIds.some((id) => !committedSet.has(id));
  }, [committedIds, stagedIds]);

  const save = useCallback(async () => {
    setSaving(true);
    const { error } = await bulkSetBlockList(listType, stagedIds.map(Number));
    setSaving(false);
    if (error) {
      setSnack({ message: extractApiErrorMessage(error, 'Could not save block list'), type: 'error', open: true });
      return;
    }
    const stagedSet = new Set(stagedIds);
    setInternalUsers((prev) =>
      prev.map((user) => ({ ...user, block_matrix: { ...user.block_matrix, [listType]: stagedSet.has(user.id) } }))
    );
    setCommittedIds(stagedIds);
    setSnack({ message: 'Block list saved', type: 'success', open: true });
  }, [listType, stagedIds, setSnack]);

  const cancel = useCallback(() => setStagedIds(committedIds), [committedIds]);

  return {
    internalUsers,
    listType,
    stagedIds,
    loading,
    saving,
    dirty,
    setStagedIds,
    selectListType,
    save,
    cancel
  };
};
